import { logError } from '../shared/utils/logger';
import { BASE_URL, fetchWithAuth } from './client';

/**
 * Claw9 API Module
 * Handles Chat messaging and CRM/Leads management for Claw9 integration
 */

// Note: Account ID should be fetched dynamically using fetchClaw9Accounts()
// The default is only used as fallback
const CLAW9_DEFAULT_ACCOUNT_ID = 'claw9care';

const parseApiErrorMessage = async (response, fallbackMessage) => {
    try {
        const errorData = await response.json();
        return (
            errorData?.detail?.message ||
            errorData?.detail?.error ||
            errorData?.error?.message ||
            errorData?.message ||
            fallbackMessage
        );
    } catch {
        const errorText = await response.text().catch(() => '');
        return errorText || fallbackMessage;
    }
};

// ==================== ACCOUNTS API ====================

/**
 * Fetch user's Zalo/Claw9 accounts
 * This should be called on login/page load to get the user's account(s)
 * @returns {Promise<{accounts: Array<{account_id: string, display_name: string, status: string}>, total: number}>}
 */
export async function fetchClaw9Accounts() {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/zalo/accounts`,
            {
                headers: { accept: 'application/json' },
                cache: 'no-store'
            }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] Accounts fetch failed:', response.status, errorText);
            logError('Claw9 accounts fetch failed:', response.status, errorText);
            throw new Error('Unable to load accounts. Please try again.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('Claw9 accounts error:', error);
        throw error;
    }
}

// ==================== CHAT APIs ====================

/**
 * Fetch conversations with optional filters
 * @param {Object} params - Query parameters
 * @param {string} params.accountId - Account identifier
 * @param {string} [params.channel] - Filter by channel (zalo, telegram, facebook, whatsapp)
 * @param {string} [params.status] - Filter by status (open, paused, closed)
 * @param {string} [params.searchName] - Search by contact name
 * @param {string} [params.cursor] - Pagination cursor
 * @param {number} [params.limit] - Results per page (default 20)
 * @returns {Promise<{conversations: Array, cursor: string|null, hasMore: boolean}>}
 */
export async function fetchClaw9Conversations(params = {}) {
    const {
        accountId = CLAW9_DEFAULT_ACCOUNT_ID,
        channel,
        status,
        searchName,
        cursor,
        limit = 20
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('account_id', accountId);
    if (channel && channel !== 'all') queryParams.append('channel', channel);
    if (status && status !== 'all') queryParams.append('status', status);
    if (searchName) queryParams.append('search_name', searchName);
    if (cursor) queryParams.append('cursor', cursor);
    queryParams.append('limit', String(limit));

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/messaging/conversations?${queryParams.toString()}`,
            {
                headers: { accept: 'application/json' },
                cache: 'no-store'
            }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] Conversations fetch failed:', response.status, errorText);
            logError('Claw9 conversations fetch failed:', response.status, errorText);
            throw new Error('Unable to load conversations. Please try again.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('Claw9 conversations error:', error);
        throw error;
    }
}

/**
 * Fetch conversation detail including contact info
 * @param {string} channel - Channel type
 * @param {string} threadId - Thread identifier
 * @param {string} [accountId] - Account identifier
 * @returns {Promise<Object>} Conversation details
 */
export async function fetchClaw9ConversationDetail(channel, threadId, accountId = CLAW9_DEFAULT_ACCOUNT_ID) {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/messaging/conversations/${channel}/${threadId}?account_id=${accountId}`,
            { headers: { accept: 'application/json' } }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            logError('Claw9 conversation detail fetch failed:', response.status, errorText);
            throw new Error('Unable to load conversation details.');
        }

        return await response.json();
    } catch (error) {
        logError('Claw9 conversation detail error:', error);
        throw error;
    }
}

/**
 * Update conversation status or assignment
 * @param {string} channel - Channel type
 * @param {string} threadId - Thread identifier
 * @param {string} accountId - Account identifier
 * @param {Object} data - Update data (status, assigned_to)
 * @returns {Promise<Object>} Updated conversation
 */
export async function updateClaw9Conversation(channel, threadId, accountId = CLAW9_DEFAULT_ACCOUNT_ID, data) {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/messaging/conversations/${channel}/${threadId}?account_id=${accountId}`,
            {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            logError('Claw9 conversation update failed:', response.status, errorText);
            throw new Error('Unable to update conversation.');
        }

        return await response.json();
    } catch (error) {
        logError('Claw9 conversation update error:', error);
        throw error;
    }
}

/**
 * Fetch messages for a conversation
 * @param {string} channel - Channel type
 * @param {string} threadId - Thread identifier
 * @param {Object} params - Query parameters
 * @param {string} params.accountId - Account identifier
 * @param {string} [params.cursor] - Pagination cursor (for older messages)
 * @param {number} [params.limit] - Messages per page (default 50)
 * @returns {Promise<{messages: Array, cursor: string|null, hasMore: boolean}>}
 */
export async function fetchClaw9Messages(channel, threadId, params = {}) {
    const { accountId = CLAW9_DEFAULT_ACCOUNT_ID, cursor, limit = 50 } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('account_id', accountId);
    if (cursor) queryParams.append('cursor', cursor);
    queryParams.append('limit', String(limit));

    const url = `${BASE_URL}/api/claw9/messaging/conversations/${channel}/${threadId}/messages?${queryParams.toString()}`;

    try {
        const response = await fetchWithAuth(url, {
            headers: { accept: 'application/json' },
            cache: 'no-store'
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] Messages fetch failed:', response.status, errorText);
            logError('Claw9 messages fetch failed:', response.status, errorText);
            throw new Error('Unable to load messages.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('[Claw9 API] Messages error:', error);
        logError('Claw9 messages error:', error);
        throw error;
    }
}

/**
 * Send a message in a conversation
 * @param {string} channel - Channel type
 * @param {string} threadId - Thread identifier
 * @param {string} accountId - Account identifier
 * @param {string} content - Message content
 * @returns {Promise<Object>} Sent message
 */
export async function sendClaw9Message(channel, threadId, accountId = CLAW9_DEFAULT_ACCOUNT_ID, content) {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/messaging/conversations/${channel}/${threadId}/messages?account_id=${accountId}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ content })
            }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            logError('Claw9 send message failed:', response.status, errorText);
            throw new Error('Unable to send message.');
        }

        return await response.json();
    } catch (error) {
        logError('Claw9 send message error:', error);
        throw error;
    }
}

/**
 * Mark conversation as read
 * @param {string} channel - Channel type
 * @param {string} threadId - Thread identifier
 * @param {string} [accountId] - Account identifier
 * @returns {Promise<void>}
 */
export async function markClaw9ConversationRead(channel, threadId, accountId = CLAW9_DEFAULT_ACCOUNT_ID) {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/messaging/conversations/${channel}/${threadId}/read?account_id=${accountId}`,
            { method: 'POST' }
        );

        if (!response.ok) {
            logError('Claw9 mark read failed:', response.status);
        }
    } catch (error) {
        logError('Claw9 mark read error:', error);
    }
}

/**
 * Fetch contacts list
 * @param {Object} params - Query parameters
 * @param {string} params.accountId - Account identifier
 * @param {string} [params.channel] - Filter by channel
 * @param {string} [params.cursor] - Pagination cursor
 * @param {number} [params.limit] - Results per page
 * @returns {Promise<{contacts: Array, cursor: string|null, hasMore: boolean}>}
 */
export async function fetchClaw9Contacts(params = {}) {
    const { accountId = CLAW9_DEFAULT_ACCOUNT_ID, channel, cursor, limit = 50 } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('account_id', accountId);
    if (channel) queryParams.append('channel', channel);
    if (cursor) queryParams.append('cursor', cursor);
    queryParams.append('limit', String(limit));

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/messaging/contacts?${queryParams.toString()}`,
            { headers: { accept: 'application/json' } }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            logError('Claw9 contacts fetch failed:', response.status, errorText);
            throw new Error('Unable to load contacts.');
        }

        return await response.json();
    } catch (error) {
        logError('Claw9 contacts error:', error);
        throw error;
    }
}

/**
 * Update contact information
 * @param {string} channel - Channel type
 * @param {string} contactId - Contact identifier
 * @param {string} accountId - Account identifier
 * @param {Object} data - Update data (notes, tags, etc.)
 * @returns {Promise<Object>} Updated contact
 */
export async function updateClaw9Contact(channel, contactId, accountId = CLAW9_DEFAULT_ACCOUNT_ID, data) {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/messaging/contacts/${channel}/${contactId}?account_id=${accountId}`,
            {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            logError('Claw9 contact update failed:', response.status, errorText);
            throw new Error('Unable to update contact.');
        }

        return await response.json();
    } catch (error) {
        logError('Claw9 contact update error:', error);
        throw error;
    }
}

// ==================== CRM/LEADS APIs ====================

/**
 * Fetch leads with optional filters
 * @param {Object} params - Query parameters
 * @param {string} params.accountId - Account identifier
 * @param {string} [params.status] - Filter by status (new, contacted, qualified, negotiating, converted, lost)
 * @param {string} [params.source] - Filter by source
 * @param {string} [params.assignedTo] - Filter by assigned user
 * @param {string} [params.cursor] - Pagination cursor
 * @param {number} [params.limit] - Results per page (default 50)
 * @returns {Promise<{leads: Array, cursor: string|null, hasMore: boolean}>}
 */
export async function fetchClaw9Leads(params = {}) {
    const {
        accountId = CLAW9_DEFAULT_ACCOUNT_ID,
        status,
        source,
        assignedTo,
        cursor,
        limit = 50
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('account_id', accountId);
    if (status && status !== 'all') queryParams.append('status', status);
    if (source && source !== 'all') queryParams.append('source', source);
    if (assignedTo) queryParams.append('assigned_to', assignedTo);
    if (cursor) queryParams.append('cursor', cursor);
    queryParams.append('limit', String(limit));

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/leads?${queryParams.toString()}`,
            {
                headers: { accept: 'application/json' },
                cache: 'no-store'
            }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] Leads fetch failed:', response.status, errorText);
            logError('Claw9 leads fetch failed:', response.status, errorText);
            throw new Error('Unable to load leads. Please try again.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('Claw9 leads error:', error);
        throw error;
    }
}

/**
 * Fetch lead pipeline statistics
 * @param {string} [accountId] - Account identifier
 * @returns {Promise<{stats: {new: number, contacted: number, qualified: number, negotiating: number, converted: number, lost: number, total: number}}>}
 */
export async function fetchClaw9LeadStats(accountId = CLAW9_DEFAULT_ACCOUNT_ID) {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/leads/stats?account_id=${accountId}`,
            {
                headers: { accept: 'application/json' },
                cache: 'no-store'
            }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] Lead stats fetch failed:', response.status, errorText);
            logError('Claw9 lead stats fetch failed:', response.status, errorText);
            throw new Error('Unable to load lead statistics.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('Claw9 lead stats error:', error);
        throw error;
    }
}

/**
 * Create a new lead manually
 * @param {string} accountId - Account identifier
 * @param {Object} data - Lead data
 * @param {string} data.name - Contact name
 * @param {string} [data.phone] - Phone number
 * @param {string} [data.email] - Email address
 * @param {string} [data.source] - Lead source
 * @param {string} [data.notes] - Initial notes
 * @param {string[]} [data.tags] - Tags
 * @returns {Promise<Object>} Created lead
 */
export async function createClaw9Lead(accountId = CLAW9_DEFAULT_ACCOUNT_ID, data) {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/leads?account_id=${accountId}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            logError('Claw9 create lead failed:', response.status, errorText);
            throw new Error('Unable to create lead.');
        }

        return await response.json();
    } catch (error) {
        logError('Claw9 create lead error:', error);
        throw error;
    }
}

/**
 * Fetch a single lead by ID
 * @param {string} leadId - Lead identifier
 * @param {string} [accountId] - Account identifier
 * @returns {Promise<Object>} Lead details
 */
export async function fetchClaw9Lead(leadId, accountId = CLAW9_DEFAULT_ACCOUNT_ID) {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/leads/${leadId}?account_id=${accountId}`,
            { headers: { accept: 'application/json' } }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            logError('Claw9 lead fetch failed:', response.status, errorText);
            throw new Error('Unable to load lead details.');
        }

        return await response.json();
    } catch (error) {
        logError('Claw9 lead fetch error:', error);
        throw error;
    }
}

/**
 * Update lead information
 * @param {string} leadId - Lead identifier
 * @param {string} accountId - Account identifier
 * @param {Object} data - Update data (status, notes, tags, assigned_to, etc.)
 * @returns {Promise<Object>} Updated lead
 */
export async function updateClaw9Lead(leadId, accountId = CLAW9_DEFAULT_ACCOUNT_ID, data) {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/leads/${leadId}?account_id=${accountId}`,
            {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            logError('Claw9 lead update failed:', response.status, errorText);
            throw new Error('Unable to update lead.');
        }

        return await response.json();
    } catch (error) {
        logError('Claw9 lead update error:', error);
        throw error;
    }
}

/**
 * Quick action: Mark lead as contacted
 * @param {string} leadId - Lead identifier
 * @param {string} [accountId] - Account identifier
 * @returns {Promise<Object>} Updated lead
 */
export async function markClaw9LeadContacted(leadId, accountId = CLAW9_DEFAULT_ACCOUNT_ID) {
    return updateClaw9Lead(leadId, accountId, { status: 'contacted' });
}

/**
 * Quick action: Mark lead as qualified
 * @param {string} leadId - Lead identifier
 * @param {string} [accountId] - Account identifier
 * @returns {Promise<Object>} Updated lead
 */
export async function markClaw9LeadQualified(leadId, accountId = CLAW9_DEFAULT_ACCOUNT_ID) {
    return updateClaw9Lead(leadId, accountId, { status: 'qualified' });
}

/**
 * Quick action: Mark lead as converted
 * @param {string} leadId - Lead identifier
 * @param {string} [accountId] - Account identifier
 * @returns {Promise<Object>} Updated lead
 */
export async function markClaw9LeadConverted(leadId, accountId = CLAW9_DEFAULT_ACCOUNT_ID) {
    return updateClaw9Lead(leadId, accountId, { status: 'converted' });
}

/**
 * Quick action: Mark lead as lost
 * @param {string} leadId - Lead identifier
 * @param {string} accountId - Account identifier
 * @param {string} [notes] - Loss reason notes
 * @returns {Promise<Object>} Updated lead
 */
export async function markClaw9LeadLost(leadId, accountId = CLAW9_DEFAULT_ACCOUNT_ID, notes) {
    return updateClaw9Lead(leadId, accountId, {
        status: 'lost',
        ...(notes ? { notes } : {})
    });
}

/**
 * Lookup lead by contact information
 * @param {string} accountId - Account identifier
 * @param {string} source - Contact source/channel
 * @param {string} contactId - Contact identifier
 * @returns {Promise<Object|null>} Lead if found, null otherwise
 */
export async function lookupClaw9LeadByContact(accountId = CLAW9_DEFAULT_ACCOUNT_ID, source, contactId) {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/leads/lookup?account_id=${accountId}&source=${source}&contact_id=${contactId}`,
            { headers: { accept: 'application/json' } }
        );

        if (response.status === 404) {
            return null;
        }

        if (!response.ok) {
            // Return null instead of throwing - lookup not finding a lead is expected
            return null;
        }

        return await response.json();
    } catch (error) {
        // Silent fail - lookup errors should not crash the app
        console.warn('Lead lookup failed:', error.message);
        return null;
    }
}

// ==================== LEAD NOTES APIs ====================

/**
 * Add a note to a lead
 * @param {string} leadId - Lead identifier
 * @param {string} accountId - Account identifier
 * @param {Object} data - Note data
 * @param {string} data.note - Note content
 * @param {string} [data.note_type] - Note type: "general", "contact", "follow_up", "status_change"
 * @param {Array} [data.mentioned_users] - Array of {email, name}
 * @returns {Promise<Object>} Updated lead with new note
 */
export async function addLeadNote(leadId, accountId = CLAW9_DEFAULT_ACCOUNT_ID, data) {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/leads/${leadId}/notes?account_id=${accountId}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            logError('Add lead note failed:', response.status, errorText);
            throw new Error('Unable to add note.');
        }

        const result = await response.json();
        return result.data || result;
    } catch (error) {
        logError('Add lead note error:', error);
        throw error;
    }
}

/**
 * Edit a note on a lead
 * @param {string} leadId - Lead identifier
 * @param {number} noteIndex - Note index in the array
 * @param {string} accountId - Account identifier
 * @param {Object} data - Updated note data
 * @param {string} data.note - Updated note content
 * @returns {Promise<Object>} Updated lead
 */
export async function editLeadNote(leadId, noteIndex, accountId = CLAW9_DEFAULT_ACCOUNT_ID, data) {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/leads/${leadId}/notes/${noteIndex}?account_id=${accountId}`,
            {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            }
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            if (errorData.error?.code === 'NOT_AUTHORIZED') {
                throw new Error('You can only edit your own notes');
            }
            throw new Error('Unable to edit note.');
        }

        const result = await response.json();
        return result.data || result;
    } catch (error) {
        logError('Edit lead note error:', error);
        throw error;
    }
}

/**
 * Delete a note from a lead
 * @param {string} leadId - Lead identifier
 * @param {number} noteIndex - Note index in the array
 * @param {string} accountId - Account identifier
 * @param {string} [noteTimestamp] - Optional timestamp for fallback identification
 * @returns {Promise<Object>} Updated lead
 */
export async function deleteLeadNote(leadId, noteIndex, accountId = CLAW9_DEFAULT_ACCOUNT_ID, noteTimestamp) {
    try {
        let url = `${BASE_URL}/api/claw9/leads/${leadId}/notes/${noteIndex}?account_id=${accountId}`;
        if (noteTimestamp) {
            url += `&note_timestamp=${encodeURIComponent(noteTimestamp)}`;
        }

        const response = await fetchWithAuth(url, { method: 'DELETE' });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            if (errorData.error?.code === 'NOT_AUTHORIZED') {
                throw new Error('You can only delete your own notes');
            }
            throw new Error('Unable to delete note.');
        }

        const result = await response.json();
        return result.data || result;
    } catch (error) {
        logError('Delete lead note error:', error);
        throw error;
    }
}

/**
 * Toggle emoji reaction on a note
 * @param {string} leadId - Lead identifier
 * @param {number} noteIndex - Note index in the array
 * @param {string} accountId - Account identifier
 * @param {string} emoji - Emoji to toggle
 * @returns {Promise<Object>} Updated lead
 */
export async function toggleNoteReaction(leadId, noteIndex, accountId = CLAW9_DEFAULT_ACCOUNT_ID, emoji) {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/leads/${leadId}/notes/${noteIndex}/reactions?account_id=${accountId}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ emoji })
            }
        );

        if (!response.ok) {
            throw new Error('Unable to toggle reaction.');
        }

        const result = await response.json();
        return result.data || result;
    } catch (error) {
        logError('Toggle note reaction error:', error);
        throw error;
    }
}

// ==================== MY OFFICE APIs ====================

/**
 * Fetch user's MyOffice configuration
 * @param {string} accountId - Account identifier (required)
 * @returns {Promise<Object|null>} Office config or null if not set up
 */
export async function fetchMyOfficeConfig(accountId) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    const url = `${BASE_URL}/api/claw9/myoffice/config?account_id=${encodeURIComponent(accountId)}`;

    try {
        const response = await fetchWithAuth(
            url,
            {
                headers: { accept: 'application/json' },
                cache: 'no-store'
            }
        );


        if (response.status === 404) {
            // No office configured yet
            return null;
        }

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] MyOffice config fetch failed:', response.status, errorText);
            logError('MyOffice config fetch failed:', response.status, errorText);
            throw new Error(`Unable to load office configuration. (Status: ${response.status})`);
        }

        const data = await response.json();
        // API returns {success: true, data: {...config...}}, extract the nested data
        return data.data || data;
    } catch (error) {
        console.error('[Claw9 API] MyOffice config error:', error);
        logError('MyOffice config error:', error);
        throw error;
    }
}

/**
 * Save user's MyOffice configuration
 * @param {string} accountId - Account identifier (required)
 * @param {Object} config - Office configuration
 * @param {string} config.name - Office name
 * @param {Object} config.theme - Theme settings (category, artStyle, environment)
 * @param {Object} config.settings - Office settings
 * @param {Array} config.agents - List of agents
 * @param {Object} [config.layout] - Custom layout configuration
 * @returns {Promise<Object>} Saved config
 */
export async function saveMyOfficeConfig(accountId, config) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/myoffice/config?account_id=${encodeURIComponent(accountId)}`,
            {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(config)
            }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] MyOffice config save failed:', response.status, errorText);
            logError('MyOffice config save failed:', response.status, errorText);
            throw new Error('Unable to save office configuration.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('MyOffice config save error:', error);
        throw error;
    }
}

/**
 * Delete user's MyOffice configuration (reset office)
 * @param {string} accountId - Account identifier (required)
 * @returns {Promise<void>}
 */
export async function deleteMyOfficeConfig(accountId) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/myoffice/config?account_id=${encodeURIComponent(accountId)}`,
            { method: 'DELETE' }
        );

        if (!response.ok && response.status !== 404) {
            const errorText = await response.text().catch(() => 'Unknown error');
            logError('MyOffice config delete failed:', response.status, errorText);
            throw new Error('Unable to reset office.');
        }

    } catch (error) {
        logError('MyOffice config delete error:', error);
        throw error;
    }
}

/**
 * Fetch MyOffice milestone statistics
 * @param {string} accountId - Account identifier (required)
 * @returns {Promise<Object>} Milestone stats
 */
export async function fetchMyOfficeStats(accountId) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/myoffice/stats?account_id=${encodeURIComponent(accountId)}`,
            {
                headers: { accept: 'application/json' },
                cache: 'no-store'
            }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] MyOffice stats fetch failed:', response.status, errorText);
            logError('MyOffice stats fetch failed:', response.status, errorText);
            throw new Error('Unable to load office statistics.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('MyOffice stats error:', error);
        throw error;
    }
}

/**
 * Update MyOffice milestone statistics
 * @param {string} accountId - Account identifier (required)
 * @param {Object} stats - Stats to update
 * @returns {Promise<Object>} Updated stats
 */
export async function updateMyOfficeStats(accountId, stats) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/myoffice/stats?account_id=${encodeURIComponent(accountId)}`,
            {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(stats)
            }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            logError('MyOffice stats update failed:', response.status, errorText);
            throw new Error('Unable to update office statistics.');
        }

        return await response.json();
    } catch (error) {
        logError('MyOffice stats update error:', error);
        throw error;
    }
}

// ==================== WEBHOOK/EVENTS APIs ====================

/**
 * Fetch recent webhook events for the office
 * @param {string} accountId - Account identifier (required)
 * @param {Object} params - Query parameters
 * @param {number} [params.limit] - Max events to fetch (default 50)
 * @param {string} [params.since] - ISO timestamp to fetch events since
 * @returns {Promise<{events: Array, hasMore: boolean}>}
 */
export async function fetchWebhookEvents(accountId, params = {}) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    const { limit = 50, since } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('account_id', accountId);
    queryParams.append('limit', String(limit));
    if (since) queryParams.append('since', since);

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/webhooks/events?${queryParams.toString()}`,
            {
                headers: { accept: 'application/json' },
                cache: 'no-store'
            }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] Webhook events fetch failed:', response.status, errorText);
            logError('Webhook events fetch failed:', response.status, errorText);
            throw new Error('Unable to load events.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('Webhook events error:', error);
        throw error;
    }
}

/**
 * Get WebSocket URL for real-time webhook events
 * @param {string} accountId - Account identifier (required)
 * @returns {string} WebSocket URL
 */
export function getWebhookStreamUrl(accountId) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    // Convert HTTP base URL to WebSocket URL
    const wsBase = BASE_URL.replace(/^http/, 'ws');
    return `${wsBase}/api/claw9/webhooks/stream?account_id=${encodeURIComponent(accountId)}`;
}

// ==================== AGENTS APIs ====================

/**
 * Fetch agents for an account
 * @param {string} accountId - Account identifier (required)
 * @returns {Promise<{agents: Array<{id: string, name: string, ...}>}>}
 */
export async function fetchClaw9Agents(accountId) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    try {
        const url = `${BASE_URL}/api/claw9/myoffice/agents?account_id=${encodeURIComponent(accountId)}`;
        const response = await fetchWithAuth(url,
            {
                headers: { accept: 'application/json' },
                cache: 'no-store'
            }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] Agents fetch failed:', response.status, errorText);
            logError('Claw9 agents fetch failed:', response.status, errorText);
            throw new Error('Unable to load agents.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('Claw9 agents error:', error);
        throw error;
    }
}

/**
 * Create a new MyOffice agent
 * @param {string} accountId - Account identifier (required)
 * @param {Object} agentData - Agent data
 * @param {string} agentData.id - Agent unique ID (e.g., "emma-001")
 * @param {string} agentData.name - Agent display name
 * @param {string} [agentData.emoji] - Agent emoji character
 * @param {string[]} [agentData.channels] - Assigned channels
 * @param {Object} [agentData.avatar3D] - 3D avatar configuration
 * @returns {Promise<Object>} Created agent
 */
export async function createMyOfficeAgent(accountId, agentData) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }
    if (!agentData || !agentData.id || !agentData.name) {
        throw new Error('Agent ID and name are required');
    }

    // Transform avatar3D to flatten accessories object for backend compatibility
    const transformedData = { ...agentData };
    if (transformedData.avatar3D?.accessories && typeof transformedData.avatar3D.accessories === 'object') {
        const { accessories, ...restAvatar } = transformedData.avatar3D;
        transformedData.avatar3D = {
            ...restAvatar,
            accessories: 'none',  // Backend expects string
            glasses: accessories.glasses || 'none',
            glassesColor: accessories.glassesColor || '#1f2937',
            hat: accessories.hat || 'none',
            hatColor: accessories.hatColor || '#1f2937',
            tie: accessories.tie || 'none',
            tieColor: accessories.tieColor || '#dc2626',
            shoes: accessories.shoes || 'none',
            shoesColor: accessories.shoesColor || '#1f2937',
        };
    }

    try {
        const url = `${BASE_URL}/api/claw9/myoffice/agents?account_id=${encodeURIComponent(accountId)}`;
        const response = await fetchWithAuth(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(transformedData)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('[Claw9 API] Create agent failed:', response.status, errorData);
            throw new Error(errorData.error?.message || 'Failed to create agent');
        }

        const data = await response.json();
        return data.data || data;
    } catch (error) {
        logError('Claw9 create agent error:', error);
        throw error;
    }
}

/**
 * Update an existing MyOffice agent
 * @param {string} accountId - Account identifier (required)
 * @param {string} agentId - Agent ID to update (required)
 * @param {Object} agentData - Agent data to update
 * @returns {Promise<Object>} Updated agent
 */
export async function updateMyOfficeAgent(accountId, agentId, agentData) {
    if (!accountId || !agentId) {
        throw new Error('Account ID and Agent ID are required');
    }

    // Transform avatar3D to flatten accessories object for backend compatibility
    const transformedData = { ...agentData };
    if (transformedData.avatar3D?.accessories && typeof transformedData.avatar3D.accessories === 'object') {
        const { accessories, ...restAvatar } = transformedData.avatar3D;
        transformedData.avatar3D = {
            ...restAvatar,
            accessories: 'none',  // Backend expects string
            glasses: accessories.glasses || 'none',
            glassesColor: accessories.glassesColor || '#1f2937',
            hat: accessories.hat || 'none',
            hatColor: accessories.hatColor || '#1f2937',
            tie: accessories.tie || 'none',
            tieColor: accessories.tieColor || '#dc2626',
            shoes: accessories.shoes || 'none',
            shoesColor: accessories.shoesColor || '#1f2937',
        };
    }

    try {
        const url = `${BASE_URL}/api/claw9/myoffice/agents/${encodeURIComponent(agentId)}?account_id=${encodeURIComponent(accountId)}`;
        const response = await fetchWithAuth(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(transformedData)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('[Claw9 API] Update agent failed:', response.status, errorData);
            throw new Error(errorData.error?.message || 'Failed to update agent');
        }

        const data = await response.json();
        return data.data || data;
    } catch (error) {
        logError('Claw9 update agent error:', error);
        throw error;
    }
}

/**
 * Delete a MyOffice agent
 * @param {string} accountId - Account identifier (required)
 * @param {string} agentId - Agent ID to delete (required)
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteMyOfficeAgent(accountId, agentId) {
    if (!accountId || !agentId) {
        throw new Error('Account ID and Agent ID are required');
    }

    try {
        const url = `${BASE_URL}/api/claw9/myoffice/agents/${encodeURIComponent(agentId)}?account_id=${encodeURIComponent(accountId)}`;
        const response = await fetchWithAuth(url, {
            method: 'DELETE'
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('[Claw9 API] Delete agent failed:', response.status, errorData);
            throw new Error(errorData.error?.message || 'Failed to delete agent');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('Claw9 delete agent error:', error);
        throw error;
    }
}

// ==================== FAQ/KNOWLEDGE BASE APIs ====================

// -------------------- ACCOUNT-LEVEL FAQs (RECOMMENDED) --------------------
// FAQs are managed at the account level, not agent level.
// Use these functions instead of the agent-level ones below.

/**
 * Fetch all FAQs for an account (account-level, no agent required)
 * @param {string} accountId - Account identifier (required)
 * @returns {Promise<{data: {faqs: Array, total_count: number, published_count: number, draft_count: number}}>}
 */
export async function fetchAccountFAQs(accountId, category) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    try {
        const queryParams = new URLSearchParams({ account_id: accountId });
        if (category) {
            queryParams.append('category', category);
        }
        const url = `${BASE_URL}/api/claw9/faqs?${queryParams.toString()}`;
        const response = await fetchWithAuth(url, {
            headers: { accept: 'application/json' },
            cache: 'no-store'
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] Account FAQs fetch failed:', response.status, errorText);
            logError('Account FAQs fetch failed:', response.status, errorText);
            throw new Error('Unable to load FAQs.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('Account FAQs error:', error);
        throw error;
    }
}

/**
 * Create a new FAQ at account level
 * @param {string} accountId - Account identifier (required)
 * @param {Object} data - FAQ data
 * @param {string} data.question - Question text
 * @param {string} data.answer - Answer text
 * @returns {Promise<Object>} Created FAQ
 */
export async function createAccountFAQ(accountId, data) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    try {
        const url = `${BASE_URL}/api/claw9/faqs?account_id=${encodeURIComponent(accountId)}`;
        const response = await fetchWithAuth(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] Account FAQ create failed:', response.status, errorText);
            logError('Account FAQ create failed:', response.status, errorText);
            throw new Error('Unable to create FAQ.');
        }

        const result = await response.json();
        return result;
    } catch (error) {
        logError('Account FAQ create error:', error);
        throw error;
    }
}

/**
 * Update an existing FAQ at account level
 * @param {string} accountId - Account identifier (required)
 * @param {string} faqId - FAQ identifier (required)
 * @param {Object} data - FAQ data to update
 * @param {string} [data.question] - Updated question
 * @param {string} [data.answer] - Updated answer
 * @returns {Promise<Object>} Updated FAQ
 */
export async function updateAccountFAQ(accountId, faqId, data) {
    if (!accountId || !faqId) {
        throw new Error('Account ID and FAQ ID are required');
    }

    try {
        const url = `${BASE_URL}/api/claw9/faqs/${encodeURIComponent(faqId)}?account_id=${encodeURIComponent(accountId)}`;
        const response = await fetchWithAuth(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] Account FAQ update failed:', response.status, errorText);
            logError('Account FAQ update failed:', response.status, errorText);
            throw new Error('Unable to update FAQ.');
        }

        const result = await response.json();
        return result;
    } catch (error) {
        logError('Account FAQ update error:', error);
        throw error;
    }
}

/**
 * Delete a FAQ at account level
 * @param {string} accountId - Account identifier (required)
 * @param {string} faqId - FAQ identifier (required)
 * @returns {Promise<Object>} Delete result
 */
export async function deleteAccountFAQ(accountId, faqId) {
    if (!accountId || !faqId) {
        throw new Error('Account ID and FAQ ID are required');
    }

    try {
        const url = `${BASE_URL}/api/claw9/faqs/${encodeURIComponent(faqId)}?account_id=${encodeURIComponent(accountId)}`;
        const response = await fetchWithAuth(url, {
            method: 'DELETE'
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] Account FAQ delete failed:', response.status, errorText);
            logError('Account FAQ delete failed:', response.status, errorText);
            throw new Error('Unable to delete FAQ.');
        }

        const result = await response.json();
        return result;
    } catch (error) {
        logError('Account FAQ delete error:', error);
        throw error;
    }
}

/**
 * Publish FAQs at account level
 * @param {string} accountId - Account identifier (required)
 * @param {Array<string>} [faqIds] - Optional list of FAQ IDs to publish. If omitted, publishes all drafts.
 * @returns {Promise<Object>} Response with published FAQs
 */
export async function publishAccountFAQs(accountId, faqIds = null) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    try {
        const url = `${BASE_URL}/api/claw9/faqs/publish?account_id=${encodeURIComponent(accountId)}`;
        const response = await fetchWithAuth(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(faqIds ? { faq_ids: faqIds } : {})
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] Account FAQs publish failed:', response.status, errorText);
            logError('Account FAQs publish failed:', response.status, errorText);
            throw new Error('Unable to publish FAQs.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('Account FAQs publish error:', error);
        throw error;
    }
}

/**
 * Bulk create FAQs at account level
 * @param {string} accountId - Account identifier (required)
 * @param {Array<{question: string, answer: string}>} faqs - FAQs to create
 * @param {boolean} [autoPublish=false] - Whether to auto-publish created FAQs
 * @returns {Promise<Object>} Response with created FAQs
 */
export async function bulkCreateAccountFAQs(accountId, faqs, autoPublish = false) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    try {
        const url = `${BASE_URL}/api/claw9/faqs/bulk?account_id=${encodeURIComponent(accountId)}`;
        const response = await fetchWithAuth(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ faqs, auto_publish: autoPublish })
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] Account FAQs bulk create failed:', response.status, errorText);
            logError('Account FAQs bulk create failed:', response.status, errorText);
            throw new Error('Unable to create FAQs.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('Account FAQs bulk create error:', error);
        throw error;
    }
}

// ==================== FAQ SELF-IMPROVEMENT APIs ====================

export async function fetchFaqSuggestions(params = {}) {
    const queryParams = new URLSearchParams();

    if (params.status) queryParams.append('status', params.status);
    if (params.priority) queryParams.append('priority', params.priority);
    if (params.limit) queryParams.append('limit', String(params.limit));
    if (params.offset) queryParams.append('offset', String(params.offset));

    const url = `${BASE_URL}/api/claw9/faq-suggestions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    try {
        const response = await fetchWithAuth(url, {
            headers: { accept: 'application/json' },
            cache: 'no-store'
        });

        if (!response.ok) {
            const message = await parseApiErrorMessage(response, 'Unable to load FAQ suggestions.');
            console.error('[Claw9 API] FAQ suggestions fetch failed:', response.status, message);
            logError('FAQ suggestions fetch failed:', response.status, message);
            throw new Error(message);
        }

        return await response.json();
    } catch (error) {
        logError('FAQ suggestions error:', error);
        throw error;
    }
}

export async function fetchFaqSuggestionStats() {
    try {
        const response = await fetchWithAuth(`${BASE_URL}/api/claw9/faq-suggestions/stats`, {
            headers: { accept: 'application/json' },
            cache: 'no-store'
        });

        if (!response.ok) {
            const message = await parseApiErrorMessage(response, 'Unable to load FAQ suggestion stats.');
            console.error('[Claw9 API] FAQ suggestion stats fetch failed:', response.status, message);
            logError('FAQ suggestion stats fetch failed:', response.status, message);
            throw new Error(message);
        }

        return await response.json();
    } catch (error) {
        logError('FAQ suggestion stats error:', error);
        throw error;
    }
}

export async function fetchFaqRefusalStats(days = 7) {
    const queryParams = new URLSearchParams({ days: String(days) });

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/faq-suggestions/refusal-stats?${queryParams.toString()}`,
            {
                headers: { accept: 'application/json' },
                cache: 'no-store'
            }
        );

        if (!response.ok) {
            const message = await parseApiErrorMessage(response, 'Unable to load refusal stats.');
            console.error('[Claw9 API] FAQ refusal stats fetch failed:', response.status, message);
            logError('FAQ refusal stats fetch failed:', response.status, message);
            throw new Error(message);
        }

        return await response.json();
    } catch (error) {
        logError('FAQ refusal stats error:', error);
        throw error;
    }
}

export async function analyzeFaqSuggestions() {
    try {
        const response = await fetchWithAuth(`${BASE_URL}/api/claw9/faq-suggestions/analyze`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({})
        });

        if (!response.ok) {
            const message = await parseApiErrorMessage(response, 'Unable to start FAQ analysis.');
            console.error('[Claw9 API] FAQ analyze failed:', response.status, message);
            logError('FAQ analyze failed:', response.status, message);
            throw new Error(message);
        }

        return await response.json();
    } catch (error) {
        logError('FAQ analyze error:', error);
        throw error;
    }
}

export async function fetchFaqSuggestion(suggestionId) {
    if (!suggestionId) {
        throw new Error('Suggestion ID is required');
    }

    try {
        const response = await fetchWithAuth(`${BASE_URL}/api/claw9/faq-suggestions/${encodeURIComponent(suggestionId)}`, {
            headers: { accept: 'application/json' },
            cache: 'no-store'
        });

        if (!response.ok) {
            const message = await parseApiErrorMessage(response, 'Unable to load suggestion detail.');
            console.error('[Claw9 API] FAQ suggestion detail fetch failed:', response.status, message);
            logError('FAQ suggestion detail fetch failed:', response.status, message);
            throw new Error(message);
        }

        return await response.json();
    } catch (error) {
        logError('FAQ suggestion detail error:', error);
        throw error;
    }
}

export async function updateFaqSuggestion(suggestionId, data) {
    if (!suggestionId) {
        throw new Error('Suggestion ID is required');
    }

    try {
        const response = await fetchWithAuth(`${BASE_URL}/api/claw9/faq-suggestions/${encodeURIComponent(suggestionId)}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });

        if (!response.ok) {
            const message = await parseApiErrorMessage(response, 'Failed to update suggestion.');
            console.error('[Claw9 API] FAQ suggestion update failed:', response.status, message);
            logError('FAQ suggestion update failed:', response.status, message);
            throw new Error(message);
        }

        return await response.json();
    } catch (error) {
        logError('FAQ suggestion update error:', error);
        throw error;
    }
}

export async function reviewFaqSuggestion(suggestionId, data) {
    if (!suggestionId) {
        throw new Error('Suggestion ID is required');
    }

    try {
        const response = await fetchWithAuth(`${BASE_URL}/api/claw9/faq-suggestions/${encodeURIComponent(suggestionId)}/review`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });

        if (!response.ok) {
            const message = await parseApiErrorMessage(response, 'Failed to review suggestion.');
            console.error('[Claw9 API] FAQ suggestion review failed:', response.status, message);
            logError('FAQ suggestion review failed:', response.status, message);
            throw new Error(message);
        }

        return await response.json();
    } catch (error) {
        logError('FAQ suggestion review error:', error);
        throw error;
    }
}

export async function promoteFaqSuggestion(suggestionId, faqId) {
    if (!suggestionId) {
        throw new Error('Suggestion ID is required');
    }

    try {
        const response = await fetchWithAuth(`${BASE_URL}/api/claw9/faq-suggestions/${encodeURIComponent(suggestionId)}/promote`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ faq_id: faqId })
        });

        if (!response.ok) {
            const message = await parseApiErrorMessage(response, 'Failed to promote suggestion.');
            console.error('[Claw9 API] FAQ suggestion promote failed:', response.status, message);
            logError('FAQ suggestion promote failed:', response.status, message);
            throw new Error(message);
        }

        return await response.json();
    } catch (error) {
        logError('FAQ suggestion promote error:', error);
        throw error;
    }
}

// -------------------- AGENT-LEVEL FAQs (LEGACY) --------------------
// These functions are kept for backward compatibility but are deprecated.
// Use the account-level functions above instead.

/**
 * @deprecated Use fetchAccountFAQs instead - FAQs are at account level, not agent level
 * Fetch FAQs for an agent
 * @param {string} accountId - Account identifier (required)
 * @param {string} agentId - Agent identifier (required)
 * @returns {Promise<{faqs: Array<{question: string, answer: string}>}>}
 */
export async function fetchClaw9FAQs(accountId, agentId, category) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }
    if (!agentId) {
        throw new Error('Agent ID is required');
    }

    try {
        const queryParams = new URLSearchParams({ account_id: accountId });
        if (category) {
            queryParams.append('category', category);
        }
        const url = `${BASE_URL}/api/claw9/agents/${encodeURIComponent(agentId)}/faqs?${queryParams.toString()}`;
        const response = await fetchWithAuth(url, {
            headers: { accept: 'application/json' },
            cache: 'no-store'
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] FAQs fetch failed:', response.status, errorText);
            logError('Claw9 FAQs fetch failed:', response.status, errorText);
            throw new Error('Unable to load FAQs.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('Claw9 FAQs error:', error);
        throw error;
    }
}

/**
 * Update an existing FAQ
 * @param {string} accountId - Account identifier (required)
 * @param {string} agentId - Agent identifier (required)
 * @param {string} faqId - FAQ identifier (required)
 * @param {Object} data - FAQ data to update
 * @param {string} [data.question] - Updated question
 * @param {string} [data.answer] - Updated answer
 * @returns {Promise<Object>} Updated FAQ
 */
export async function updateClaw9FAQ(accountId, agentId, faqId, data) {
    if (!accountId || !agentId || !faqId) {
        throw new Error('Account ID, Agent ID, and FAQ ID are required');
    }

    try {
        const url = `${BASE_URL}/api/claw9/agents/${encodeURIComponent(agentId)}/faqs/${encodeURIComponent(faqId)}?account_id=${encodeURIComponent(accountId)}`;
        const response = await fetchWithAuth(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] FAQ update failed:', response.status, errorText);
            logError('Claw9 FAQ update failed:', response.status, errorText);
            throw new Error('Unable to update FAQ.');
        }

        const result = await response.json();
        return result;
    } catch (error) {
        logError('Claw9 FAQ update error:', error);
        throw error;
    }
}

/**
 * Create a new FAQ
 * @param {string} accountId - Account identifier (required)
 * @param {string} agentId - Agent identifier (required)
 * @param {Object} data - FAQ data
 * @param {string} data.question - Question text
 * @param {string} data.answer - Answer text
 * @returns {Promise<Object>} Created FAQ
 */
export async function createClaw9FAQ(accountId, agentId, data) {
    if (!accountId || !agentId) {
        throw new Error('Account ID and Agent ID are required');
    }

    try {
        const url = `${BASE_URL}/api/claw9/agents/${encodeURIComponent(agentId)}/faqs?account_id=${encodeURIComponent(accountId)}`;
        const response = await fetchWithAuth(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] FAQ create failed:', response.status, errorText);
            logError('Claw9 FAQ create failed:', response.status, errorText);
            throw new Error('Unable to create FAQ.');
        }

        const result = await response.json();
        return result;
    } catch (error) {
        logError('Claw9 FAQ create error:', error);
        throw error;
    }
}

/**
 * Generate FAQ variations for an agent-scoped FAQ draft
 * @param {string} accountId - Account identifier (required)
 * @param {string} agentId - Agent identifier (required)
 * @param {Object} data - FAQ draft data
 * @param {string} data.question - Question text
 * @param {string} data.answer - Answer text
 * @param {string} data.category - FAQ category
 * @param {number} [data.count=5] - Number of variations to request
 * @returns {Promise<Object>} Generated variations payload
 */
export async function generateClaw9FAQVariations(accountId, agentId, data) {
    if (!accountId || !agentId) {
        throw new Error('Account ID and Agent ID are required');
    }

    try {
        const url = `${BASE_URL}/api/claw9/agents/${encodeURIComponent(agentId)}/faqs/generate-variations?account_id=${encodeURIComponent(accountId)}`;
        const response = await fetchWithAuth(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] FAQ variation generation failed:', response.status, errorText);
            logError('Claw9 FAQ variation generation failed:', response.status, errorText);
            throw new Error('Unable to generate FAQ variations.');
        }

        const result = await response.json();
        return result;
    } catch (error) {
        logError('Claw9 FAQ variation generation error:', error);
        throw error;
    }
}

/**
 * Delete a FAQ
 * @param {string} accountId - Account identifier (required)
 * @param {string} agentId - Agent identifier (required)
 * @param {string} faqId - FAQ identifier (required)
 * @returns {Promise<Object>} Delete result
 */
export async function deleteClaw9FAQ(accountId, agentId, faqId) {
    if (!accountId || !agentId || !faqId) {
        throw new Error('Account ID, Agent ID, and FAQ ID are required');
    }

    try {
        const url = `${BASE_URL}/api/claw9/agents/${encodeURIComponent(agentId)}/faqs/${encodeURIComponent(faqId)}?account_id=${encodeURIComponent(accountId)}`;
        const response = await fetchWithAuth(url, {
            method: 'DELETE'
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] FAQ delete failed:', response.status, errorText);
            logError('Claw9 FAQ delete failed:', response.status, errorText);
            throw new Error('Unable to delete FAQ.');
        }

        const result = await response.json();
        return result;
    } catch (error) {
        logError('Claw9 FAQ delete error:', error);
        throw error;
    }
}

/**
 * Publish FAQs for an agent (only publishes existing draft FAQs)
 * @param {string} accountId - Account identifier (required)
 * @param {string} agentId - Agent identifier (required)
 * @param {Array<string>} [faqIds] - Optional list of FAQ IDs to publish. If omitted, publishes all drafts.
 * @returns {Promise<Object>} Response with published FAQs
 */
export async function publishClaw9FAQs(accountId, agentId, faqIds = null) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }
    if (!agentId) {
        throw new Error('Agent ID is required');
    }

    try {
        const url = `${BASE_URL}/api/claw9/agents/${encodeURIComponent(agentId)}/faqs/publish?account_id=${encodeURIComponent(accountId)}`;
        const response = await fetchWithAuth(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(faqIds ? { faq_ids: faqIds } : {})
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] FAQs publish failed:', response.status, errorText);
            logError('Claw9 FAQs publish failed:', response.status, errorText);
            throw new Error('Unable to publish FAQs.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('Claw9 FAQs publish error:', error);
        throw error;
    }
}

/**
 * Bulk create FAQs (for importing multiple FAQs at once)
 * @param {string} accountId - Account identifier (required)
 * @param {string} agentId - Agent identifier (required)
 * @param {Array<{question: string, answer: string}>} faqs - FAQs to create
 * @param {boolean} [autoPublish=false] - Whether to auto-publish created FAQs
 * @returns {Promise<Object>} Response with created FAQs
 */
export async function bulkCreateClaw9FAQs(accountId, agentId, faqs, autoPublish = false) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }
    if (!agentId) {
        throw new Error('Agent ID is required');
    }

    try {
        const url = `${BASE_URL}/api/claw9/agents/${encodeURIComponent(agentId)}/faqs/bulk?account_id=${encodeURIComponent(accountId)}`;
        const response = await fetchWithAuth(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ faqs, auto_publish: autoPublish })
        });

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] FAQs bulk create failed:', response.status, errorText);
            logError('Claw9 FAQs bulk create failed:', response.status, errorText);
            throw new Error('Unable to create FAQs.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('Claw9 FAQs bulk create error:', error);
        throw error;
    }
}

// ==================== BOT AGENTS APIs ====================

/**
 * Fetch all bot agents
 * @returns {Promise<{success: boolean, agents: Array<{name: string, display_name: string, workspace: string, model: string, enabled: boolean, bindings: Array, is_default: boolean}>}>}
 */
export async function fetchBotAgents() {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/bots/agents/list`,
            {
                headers: { accept: 'application/json' },
                cache: 'no-store'
            }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] Bot agents fetch failed:', response.status, errorText);
            logError('Bot agents fetch failed:', response.status, errorText);
            throw new Error('Unable to load bot agents.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('Bot agents error:', error);
        throw error;
    }
}

/**
 * Get bot agent status
 * @param {string} accountId - Agent account identifier (required)
 * @returns {Promise<{success: boolean, account_id: string, enabled: boolean, bindings: Array, message: string}>}
 */
export async function getBotAgentStatus(accountId) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/bots/${encodeURIComponent(accountId)}/agent-status`,
            {
                headers: { accept: 'application/json' },
                cache: 'no-store'
            }
        );

        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('[Claw9 API] Bot agent status fetch failed:', response.status, errorText);
            logError('Bot agent status fetch failed:', response.status, errorText);
            throw new Error('Unable to get agent status.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('Bot agent status error:', error);
        throw error;
    }
}

/**
 * Toggle bot agent enabled/disabled
 * @param {string} accountId - Agent account identifier (required)
 * @param {boolean} enabled - Whether to enable or disable the agent
 * @returns {Promise<{success: boolean, account_id: string, enabled: boolean, bindings: Array, message: string}>}
 */
export async function toggleBotAgent(accountId, enabled) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/bots/${encodeURIComponent(accountId)}/toggle`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ enabled })
            }
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('[Claw9 API] Bot agent toggle failed:', response.status, errorData);
            throw new Error(errorData.detail || 'Unable to toggle agent.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('Bot agent toggle error:', error);
        throw error;
    }
}

/**
 * Enable bot agent (shortcut)
 * @param {string} accountId - Agent account identifier (required)
 * @returns {Promise<Object>}
 */
export async function enableBotAgent(accountId) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/bots/${encodeURIComponent(accountId)}/enable`,
            { method: 'POST' }
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('[Claw9 API] Bot agent enable failed:', response.status, errorData);
            throw new Error(errorData.detail || 'Unable to enable agent.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('Bot agent enable error:', error);
        throw error;
    }
}

/**
 * Disable bot agent (shortcut)
 * @param {string} accountId - Agent account identifier (required)
 * @returns {Promise<Object>}
 */
export async function disableBotAgent(accountId) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/bots/${encodeURIComponent(accountId)}/disable`,
            { method: 'POST' }
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            console.error('[Claw9 API] Bot agent disable failed:', response.status, errorData);
            throw new Error(errorData.detail || 'Unable to disable agent.');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        logError('Bot agent disable error:', error);
        throw error;
    }
}

// ==================== ZALO MESSAGING APIs ====================

/**
 * Send message to a lead via Zalo
 * @param {string} leadId - The lead's ID from the CRM
 * @param {string} message - Message text (1-5000 chars)
 * @param {string} [accountId='claw9care'] - Sender account
 * @returns {Promise<{success: boolean, message_id?: string, error?: string}>}
 */
export async function sendZaloMessageToLead(leadId, message, accountId = 'claw9care') {
    if (!leadId) {
        throw new Error('Lead ID is required');
    }
    if (!message || message.trim().length === 0) {
        throw new Error('Message is required');
    }

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/messaging/send-to-lead`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    lead_id: leadId,
                    message: message.trim(),
                    account_id: accountId
                })
            }
        );

        const data = await response.json();

        if (!response.ok) {
            console.error('[Claw9 API] Send message to lead failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to send message'
            };
        }

        return data;
    } catch (error) {
        logError('Send message to lead error:', error);
        return {
            success: false,
            error: error.message || 'Failed to send message'
        };
    }
}

/**
 * Send message directly to a Zalo user ID
 * @param {string} zaloUserId - Recipient's Zalo user ID
 * @param {string} message - Message text (1-5000 chars)
 * @param {string} [accountId='claw9care'] - Sender account
 * @returns {Promise<{success: boolean, message_id?: string, error?: string}>}
 */
export async function sendZaloMessageDirect(zaloUserId, message, accountId = 'claw9care') {
    if (!zaloUserId) {
        throw new Error('Zalo User ID is required');
    }
    if (!message || message.trim().length === 0) {
        throw new Error('Message is required');
    }

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/messaging/send-direct`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    zalo_user_id: zaloUserId,
                    message: message.trim(),
                    account_id: accountId
                })
            }
        );

        const data = await response.json();

        if (!response.ok) {
            console.error('[Claw9 API] Send direct message failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to send message'
            };
        }

        return data;
    } catch (error) {
        logError('Send direct message error:', error);
        return {
            success: false,
            error: error.message || 'Failed to send message'
        };
    }
}

/**
 * Send bulk messages to multiple leads
 * @param {string[]} leadIds - Array of lead IDs (max 50)
 * @param {string} message - Message text (1-5000 chars)
 * @param {string} [accountId='claw9care'] - Sender account
 * @returns {Promise<{success: boolean, data?: {total: number, successful: number, failed: number, results: Array}}>}
 */
export async function sendZaloBulkMessage(leadIds, message, accountId = 'claw9care') {
    if (!leadIds || leadIds.length === 0) {
        throw new Error('Lead IDs are required');
    }
    if (leadIds.length > 50) {
        throw new Error('Maximum 50 leads per bulk send');
    }
    if (!message || message.trim().length === 0) {
        throw new Error('Message is required');
    }

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/messaging/send-bulk`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    lead_ids: leadIds,
                    message: message.trim(),
                    account_id: accountId
                })
            }
        );

        const data = await response.json();

        if (!response.ok) {
            console.error('[Claw9 API] Bulk send failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to send bulk messages'
            };
        }

        return data;
    } catch (error) {
        logError('Bulk send error:', error);
        return {
            success: false,
            error: error.message || 'Failed to send bulk messages'
        };
    }
}

/**
 * Get available Zalo messaging sessions/accounts
 * @returns {Promise<{success: boolean, data?: {accounts: string[]}}>}
 */
export async function getZaloMessagingSessions() {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/messaging/sessions`,
            {
                headers: { accept: 'application/json' }
            }
        );

        const data = await response.json();

        if (!response.ok) {
            console.error('[Claw9 API] Get sessions failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to get sessions'
            };
        }

        return data;
    } catch (error) {
        logError('Get sessions error:', error);
        return {
            success: false,
            error: error.message || 'Failed to get sessions'
        };
    }
}

/**
 * Get info about a specific messaging session
 * @param {string} accountId - The account ID to get info for
 * @returns {Promise<{success: boolean, data?: {account_id: string, user_id: string, has_cookies: boolean, cookie_count: number}}>}
 */
export async function getZaloSessionInfo(accountId) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/messaging/sessions/${encodeURIComponent(accountId)}`,
            {
                headers: { accept: 'application/json' }
            }
        );

        const data = await response.json();

        if (!response.ok) {
            console.error('[Claw9 API] Get session info failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to get session info'
            };
        }

        return data;
    } catch (error) {
        logError('Get session info error:', error);
        return {
            success: false,
            error: error.message || 'Failed to get session info'
        };
    }
}

// ==================== MY DOCS API ====================

/**
 * Upload a document to My Docs
 * @param {File} file - The file to upload
 * @param {Object} options - Upload options
 * @param {string} [options.source='manual'] - Source of the document
 * @param {string} [options.accountId] - Account ID
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function uploadMyDoc(file, options = {}) {
    if (!file) {
        throw new Error('File is required');
    }

    try {
        const formData = new FormData();
        formData.append('file', file);
        if (options.source) {
            formData.append('source', options.source);
        }
        if (options.accountId) {
            formData.append('account_id', options.accountId);
        }

        const response = await fetchWithAuth(
            `${BASE_URL}/api/my-docs/upload`,
            {
                method: 'POST',
                body: formData
                // Note: Don't set Content-Type header - browser will set it with boundary
            }
        );

        const data = await response.json();

        if (!response.ok) {
            console.error('[My Docs API] Upload failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to upload document'
            };
        }

        return data;
    } catch (error) {
        logError('My Docs upload error:', error);
        return {
            success: false,
            error: error.message || 'Failed to upload document'
        };
    }
}

/**
 * List documents from My Docs
 * @param {string} accountId - Account identifier (required)
 * @param {Object} params - Query parameters
 * @param {string} [params.date_from] - Filter by start date (YYYY-MM-DD)
 * @param {string} [params.date_to] - Filter by end date (YYYY-MM-DD)
 * @param {string} [params.source] - Filter by source (manual, zalo, telegram, messenger)
 * @param {string} [params.status] - Filter by summary status (processing, ready, failed)
 * @param {string} [params.search] - Search in filename
 * @returns {Promise<{success: boolean, data?: {groups: Array, total: number}, error?: string}>}
 */
export async function listMyDocs(accountId, params = {}) {
    if (!accountId) {
        return {
            success: false,
            error: 'Account ID is required'
        };
    }

    try {
        const queryParams = new URLSearchParams();
        queryParams.append('account_id', accountId);
        if (params.date_from) queryParams.append('date_from', params.date_from);
        if (params.date_to) queryParams.append('date_to', params.date_to);
        if (params.source) queryParams.append('source', params.source);
        if (params.status) queryParams.append('status', params.status);
        if (params.search) queryParams.append('search', params.search);

        const queryString = queryParams.toString();
        const url = `${BASE_URL}/api/my-docs?${queryString}`;

        const response = await fetchWithAuth(url, {
            headers: { accept: 'application/json' },
            cache: 'no-store'
        });

        const data = await response.json();

        if (!response.ok) {
            console.error('[My Docs API] List failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to load documents'
            };
        }

        return data;
    } catch (error) {
        logError('My Docs list error:', error);
        return {
            success: false,
            error: error.message || 'Failed to load documents'
        };
    }
}

/**
 * Get a single document by ID
 * @param {string} docId - Document ID
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function getMyDoc(docId) {
    if (!docId) {
        throw new Error('Document ID is required');
    }

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/my-docs/${encodeURIComponent(docId)}`,
            {
                headers: { accept: 'application/json' }
            }
        );

        const data = await response.json();

        if (!response.ok) {
            console.error('[My Docs API] Get doc failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to load document'
            };
        }

        return data;
    } catch (error) {
        logError('My Docs get error:', error);
        return {
            success: false,
            error: error.message || 'Failed to load document'
        };
    }
}

/**
 * Get signed URL for document download/preview
 * @param {string} docId - Document ID
 * @returns {Promise<{success: boolean, data?: {url: string, expires_in: number}, error?: string}>}
 */
export async function getMyDocSignedUrl(docId) {
    if (!docId) {
        throw new Error('Document ID is required');
    }

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/my-docs/${encodeURIComponent(docId)}/signed-url`,
            {
                headers: { accept: 'application/json' }
            }
        );

        const data = await response.json();

        if (!response.ok) {
            console.error('[My Docs API] Get signed URL failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to get download URL'
            };
        }

        return data;
    } catch (error) {
        logError('My Docs signed URL error:', error);
        return {
            success: false,
            error: error.message || 'Failed to get download URL'
        };
    }
}

/**
 * Delete a document
 * @param {string} docId - Document ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deleteMyDoc(docId) {
    if (!docId) {
        throw new Error('Document ID is required');
    }

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/my-docs/${encodeURIComponent(docId)}`,
            {
                method: 'DELETE',
                headers: { accept: 'application/json' }
            }
        );

        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            console.error('[My Docs API] Delete failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to delete document'
            };
        }

        return { success: true };
    } catch (error) {
        logError('My Docs delete error:', error);
        return {
            success: false,
            error: error.message || 'Failed to delete document'
        };
    }
}

// ==================== KNOWLEDGE BASE APIs ====================

/**
 * Fetch the current knowledge base for an account/agent
 * @param {string} accountId - Account identifier (required)
 * @param {string} [agentId] - Agent identifier (optional, for agent-specific knowledge)
 * @returns {Promise<{success: boolean, knowledge: Object|null, error?: string}>}
 */
export async function fetchKnowledgeBase(accountId, agentId = null) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    try {
        const queryParams = new URLSearchParams();
        queryParams.append('account_id', accountId);
        if (agentId) {
            queryParams.append('agent_id', agentId);
        }

        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/knowledge?${queryParams.toString()}`,
            {
                headers: { accept: 'application/json' },
                cache: 'no-store'
            }
        );

        if (response.status === 404) {
            // No knowledge base exists yet - this is expected for new accounts
            return { success: true, knowledge: null };
        }

        if (!response.ok) {
            const errorMsg = await parseApiErrorMessage(response, 'Failed to fetch knowledge base');
            throw new Error(errorMsg);
        }

        const data = await response.json();
        return {
            success: true,
            knowledge: data.knowledge || data
        };
    } catch (error) {
        logError('Knowledge base fetch error:', error);
        throw error;
    }
}

/**
 * Generate or regenerate the knowledge base from FAQs using Claude Opus
 * This transforms FAQs into a structured sales knowledge document
 * @param {string} accountId - Account identifier (required)
 * @param {string} [agentId] - Agent identifier (optional)
 * @param {Object} [options] - Generation options
 * @param {boolean} [options.regenerate=false] - If true, regenerates from scratch
 * @param {boolean} [options.include_diary=true] - If true, includes update diary section
 * @returns {Promise<{success: boolean, knowledge: Object, error?: string}>}
 */
export async function generateKnowledgeBase(accountId, agentId = null, options = {}) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    const { regenerate = false, include_diary = true } = options;

    try {
        const queryParams = new URLSearchParams();
        queryParams.append('account_id', accountId);
        if (agentId) {
            queryParams.append('agent_id', agentId);
        }

        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/knowledge/generate?${queryParams.toString()}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    regenerate,
                    include_diary,
                    model: 'claude-opus-4-20250514', // Use Opus for best quality
                    template: 'sales_knowledge_base', // Use the standard template
                })
            }
        );

        if (!response.ok) {
            const errorMsg = await parseApiErrorMessage(response, 'Failed to generate knowledge base');
            throw new Error(errorMsg);
        }

        const data = await response.json();
        return {
            success: true,
            knowledge: data.knowledge || data
        };
    } catch (error) {
        logError('Knowledge base generation error:', error);
        throw error;
    }
}

/**
 * Update the knowledge base content (manual edits)
 * @param {string} accountId - Account identifier (required)
 * @param {string} [agentId] - Agent identifier (optional)
 * @param {Object} updates - Update data
 * @param {string} updates.content - The new markdown content
 * @param {string} [updates.updated_by] - Who made the update (for audit)
 * @returns {Promise<{success: boolean, knowledge: Object, error?: string}>}
 */
export async function updateKnowledgeBase(accountId, agentId = null, updates = {}) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }
    if (!updates.content) {
        throw new Error('Content is required');
    }

    try {
        const queryParams = new URLSearchParams();
        queryParams.append('account_id', accountId);
        if (agentId) {
            queryParams.append('agent_id', agentId);
        }

        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/knowledge?${queryParams.toString()}`,
            {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    content: updates.content,
                    updated_by: updates.updated_by || 'user_manual_edit',
                    change_summary: updates.change_summary || 'Manual edit via UI',
                })
            }
        );

        if (!response.ok) {
            const errorMsg = await parseApiErrorMessage(response, 'Failed to update knowledge base');
            throw new Error(errorMsg);
        }

        const data = await response.json();
        return {
            success: true,
            knowledge: data.knowledge || data
        };
    } catch (error) {
        logError('Knowledge base update error:', error);
        throw error;
    }
}

/**
 * Fetch version history of the knowledge base
 * @param {string} accountId - Account identifier (required)
 * @param {string} [agentId] - Agent identifier (optional)
 * @param {number} [limit=20] - Number of versions to fetch
 * @returns {Promise<{success: boolean, history: Array, error?: string}>}
 */
export async function fetchKnowledgeHistory(accountId, agentId = null, limit = 20) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    try {
        const queryParams = new URLSearchParams();
        queryParams.append('account_id', accountId);
        if (agentId) {
            queryParams.append('agent_id', agentId);
        }
        queryParams.append('limit', String(limit));

        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/knowledge/history?${queryParams.toString()}`,
            {
                headers: { accept: 'application/json' },
                cache: 'no-store'
            }
        );

        if (!response.ok) {
            const errorMsg = await parseApiErrorMessage(response, 'Failed to fetch knowledge history');
            throw new Error(errorMsg);
        }

        const data = await response.json();
        return {
            success: true,
            history: data.history || data.versions || []
        };
    } catch (error) {
        logError('Knowledge history fetch error:', error);
        throw error;
    }
}

/**
 * Sync knowledge base to S3 for serving to customers
 * @param {string} accountId - Account identifier (required)
 * @param {string} [agentId] - Agent identifier (optional)
 * @returns {Promise<{success: boolean, s3_url: string, error?: string}>}
 */
export async function syncKnowledgeToS3(accountId, agentId = null) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    try {
        const queryParams = new URLSearchParams();
        queryParams.append('account_id', accountId);
        if (agentId) {
            queryParams.append('agent_id', agentId);
        }

        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/knowledge/sync-s3?${queryParams.toString()}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            }
        );

        if (!response.ok) {
            const errorMsg = await parseApiErrorMessage(response, 'Failed to sync knowledge to S3');
            throw new Error(errorMsg);
        }

        const data = await response.json();
        return {
            success: true,
            s3_url: data.s3_url || data.url
        };
    } catch (error) {
        logError('Knowledge S3 sync error:', error);
        throw error;
    }
}

/**
 * Get the S3 URL for the current knowledge base
 * @param {string} accountId - Account identifier (required)
 * @param {string} [agentId] - Agent identifier (optional)
 * @returns {Promise<{success: boolean, s3_url: string|null, error?: string}>}
 */
export async function getKnowledgeS3Url(accountId, agentId = null) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }

    try {
        const queryParams = new URLSearchParams();
        queryParams.append('account_id', accountId);
        if (agentId) {
            queryParams.append('agent_id', agentId);
        }

        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/knowledge/s3-url?${queryParams.toString()}`,
            {
                headers: { accept: 'application/json' },
                cache: 'no-store'
            }
        );

        if (response.status === 404) {
            return { success: true, s3_url: null };
        }

        if (!response.ok) {
            const errorMsg = await parseApiErrorMessage(response, 'Failed to get S3 URL');
            throw new Error(errorMsg);
        }

        const data = await response.json();
        return {
            success: true,
            s3_url: data.s3_url || data.url
        };
    } catch (error) {
        logError('Knowledge S3 URL fetch error:', error);
        throw error;
    }
}

/**
 * Publish FAQs AND generate/update knowledge base
 * This is the enhanced publish flow that includes knowledge generation
 * @param {string} accountId - Account identifier (required)
 * @param {string} agentId - Agent identifier (required)
 * @param {Array<string>} [faqIds] - Optional list of FAQ IDs to publish
 * @param {Object} [options] - Additional options
 * @param {boolean} [options.generate_knowledge=true] - Whether to generate knowledge after publish
 * @param {boolean} [options.sync_s3=true] - Whether to sync to S3 after generation
 * @returns {Promise<{success: boolean, published_faqs: Array, knowledge?: Object, s3_url?: string}>}
 */
export async function publishFAQsWithKnowledge(accountId, agentId, faqIds = null, options = {}) {
    if (!accountId) {
        throw new Error('Account ID is required');
    }
    if (!agentId) {
        throw new Error('Agent ID is required');
    }

    const { generate_knowledge = true, sync_s3 = true, include_scripts = true } = options;

    try {
        const url = `${BASE_URL}/api/claw9/agents/${encodeURIComponent(agentId)}/faqs/publish-with-knowledge?account_id=${encodeURIComponent(accountId)}`;

        const response = await fetchWithAuth(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                faq_ids: faqIds,
                generate_knowledge,
                sync_s3,
                include_scripts,
                knowledge_options: {
                    model: 'claude-opus-4-20250514',
                    template: 'sales_knowledge_base',
                    include_diary: true,
                }
            })
        });

        if (!response.ok) {
            const errorMsg = await parseApiErrorMessage(response, 'Failed to publish FAQs with knowledge');
            throw new Error(errorMsg);
        }

        const data = await response.json();
        return {
            success: true,
            published_faqs: data.published_faqs || [],
            knowledge: data.knowledge || null,
            s3_url: data.s3_url || null
        };
    } catch (error) {
        logError('Publish with knowledge error:', error);
        throw error;
    }
}

// ==================== TARGET SCRIPTS API ====================

/**
 * Fetch all target scripts for an account
 * @param {Object} params - Query parameters
 * @param {string} [params.accountId] - Account identifier
 * @param {string} [params.agentId] - Filter by agent
 * @param {boolean} [params.activeOnly] - Only return active scripts
 * @returns {Promise<{scripts: Array, total: number}>}
 */
export async function fetchTargetScripts(params = {}) {
    const { accountId, agentId, activeOnly } = params;

    const queryParams = new URLSearchParams();
    if (accountId) queryParams.append('account_id', accountId);
    if (agentId) queryParams.append('agent_id', agentId);
    if (activeOnly) queryParams.append('active_only', 'true');

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/scripts?${queryParams.toString()}`,
            { headers: { accept: 'application/json' } }
        );

        if (!response.ok) {
            const errorMsg = await parseApiErrorMessage(response, 'Failed to fetch scripts');
            throw new Error(errorMsg);
        }

        const data = await response.json();
        return { scripts: data.scripts || data.data || [], total: data.total || 0 };
    } catch (error) {
        logError('Fetch target scripts error:', error);
        throw error;
    }
}

/**
 * Get a single target script by ID
 * @param {string} scriptId - Script identifier
 * @param {string} [accountId] - Account identifier
 * @returns {Promise<Object>} Script object
 */
export async function fetchTargetScript(scriptId, accountId) {
    const queryParams = new URLSearchParams();
    if (accountId) queryParams.append('account_id', accountId);

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/scripts/${encodeURIComponent(scriptId)}?${queryParams.toString()}`,
            { headers: { accept: 'application/json' } }
        );

        if (!response.ok) {
            const errorMsg = await parseApiErrorMessage(response, 'Failed to fetch script');
            throw new Error(errorMsg);
        }

        const data = await response.json();
        return data.data || data;
    } catch (error) {
        logError('Fetch target script error:', error);
        throw error;
    }
}

/**
 * Create a new target script
 * @param {Object} scriptData - Script data
 * @param {string} scriptData.name - Script name
 * @param {string} [scriptData.description] - Script description
 * @param {string} scriptData.goal - Goal type (order, appointment, consultation, info, custom)
 * @param {Array<string>} scriptData.trigger_phrases - Phrases that trigger this script
 * @param {Array<Object>} scriptData.steps - Script steps
 * @param {string} [scriptData.success_message] - Message on successful completion
 * @param {string} [scriptData.cancel_message] - Message on cancel
 * @param {string} [scriptData.agent_id] - Associated agent
 * @param {string} [accountId] - Account identifier
 * @returns {Promise<Object>} Created script
 */
export async function createTargetScript(scriptData, accountId) {
    const queryParams = new URLSearchParams();
    if (accountId) queryParams.append('account_id', accountId);

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/scripts?${queryParams.toString()}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(scriptData)
            }
        );

        if (!response.ok) {
            const errorMsg = await parseApiErrorMessage(response, 'Failed to create script');
            throw new Error(errorMsg);
        }

        const data = await response.json();
        return data.data || data;
    } catch (error) {
        logError('Create target script error:', error);
        throw error;
    }
}

/**
 * Update an existing target script
 * @param {string} scriptId - Script identifier
 * @param {Object} updates - Fields to update
 * @param {string} [accountId] - Account identifier
 * @returns {Promise<Object>} Updated script
 */
export async function updateTargetScript(scriptId, updates, accountId) {
    const queryParams = new URLSearchParams();
    if (accountId) queryParams.append('account_id', accountId);

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/scripts/${encodeURIComponent(scriptId)}?${queryParams.toString()}`,
            {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updates)
            }
        );

        if (!response.ok) {
            const errorMsg = await parseApiErrorMessage(response, 'Failed to update script');
            throw new Error(errorMsg);
        }

        const data = await response.json();
        return data.data || data;
    } catch (error) {
        logError('Update target script error:', error);
        throw error;
    }
}

/**
 * Delete a target script
 * @param {string} scriptId - Script identifier
 * @param {string} [accountId] - Account identifier
 * @returns {Promise<void>}
 */
export async function deleteTargetScript(scriptId, accountId) {
    const queryParams = new URLSearchParams();
    if (accountId) queryParams.append('account_id', accountId);

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/scripts/${encodeURIComponent(scriptId)}?${queryParams.toString()}`,
            { method: 'DELETE' }
        );

        if (!response.ok) {
            const errorMsg = await parseApiErrorMessage(response, 'Failed to delete script');
            throw new Error(errorMsg);
        }
    } catch (error) {
        logError('Delete target script error:', error);
        throw error;
    }
}

/**
 * Activate or deactivate a target script
 * @param {string} scriptId - Script identifier
 * @param {boolean} isActive - Whether to activate or deactivate
 * @param {string} [accountId] - Account identifier
 * @returns {Promise<Object>} Updated script
 */
export async function toggleTargetScript(scriptId, isActive, accountId) {
    const queryParams = new URLSearchParams();
    if (accountId) queryParams.append('account_id', accountId);

    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/scripts/${encodeURIComponent(scriptId)}/activate?${queryParams.toString()}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ is_active: isActive })
            }
        );

        if (!response.ok) {
            const errorMsg = await parseApiErrorMessage(response, `Failed to ${isActive ? 'activate' : 'deactivate'} script`);
            throw new Error(errorMsg);
        }

        const data = await response.json();
        return data.data || data;
    } catch (error) {
        logError('Toggle target script error:', error);
        throw error;
    }
}
